import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useDoctors, useEncounterMutation } from '../hooks';
import { administerMed, countersignMed, createMedOrder, discontinueMed, type MedOrderInput } from '../api';
import { Section, errorInfo, timeLabel } from '../ui';
import type { MedAdministration, MedOrder, Workspace } from '../types';

import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { appendText, matchByName, numField, selectField, textField, vNum, vOneOf, vText, type Extracted } from '@/lib/voiceFill';

const ROUTES = ['IV', 'IM', 'SC', 'Oral', 'Nebulised', 'Sublingual', 'Rectal', 'Topical', 'Intranasal', 'IO'];
const FREQ = ['STAT', 'SOS', 'OD', 'BD', 'TDS', 'QID', 'Q4H', 'Q6H', 'Infusion'];

/**
 * Order ≠ administration (D7). Ordering needs er:med-order (Doctor); charting
 * needs er:med-admin (Nurse). A nurse records a resus VERBAL order on the
 * doctor's behalf — named, flagged, and listed as an open item until a doctor
 * countersigns. A medication order is never evidence of a dose given.
 */
export default function MedicationsSection({ ws }: { ws: Workspace }) {
  const { user } = useAuth();
  const id = ws.encounter.id;
  const mods = user?.modules ?? [];
  const canOrder = mods.includes('er:med-order');
  const canAdmin = mods.includes('er:med-admin');
  const open = ws.encounter.status !== 'departed';
  const doctors = useDoctors();
  const [f, setF] = useState<MedOrderInput>({ drug_name: '', frequency_code: 'STAT', route: 'IV' });
  // Stop asks for a reason in a dialog — window.prompt() is refused by embedded browsers.
  const [stopping, setStopping] = useState<MedOrder | null>(null);
  const [chart, setChart] = useState<{ order: MedOrder; status: MedAdministration['status']; reason: string; dose: string } | null>(null);

  const order = useEncounterMutation(id, (input: MedOrderInput) => createMedOrder(id, input));
  const give = useEncounterMutation(id, (v: { orderId: number; body: Parameters<typeof administerMed>[1] }) => administerMed(v.orderId, v.body));
  const stop = useEncounterMutation(id, (v: { orderId: number; reason: string }) => discontinueMed(v.orderId, v.reason));
  const sign = useEncounterMutation(id, (orderId: number) => countersignMed(orderId));

  const verbal = !canOrder && canAdmin;
  const set = <K extends keyof MedOrderInput>(k: K, v: MedOrderInput[K]) => setF((x) => ({ ...x, [k]: v }));

  // "Inj. Adrenaline 0.5 mg IM STAT" in one breath. A nurse recording a
  // verbal order can also name the doctor ("verbal order from Dr. Menon"),
  // matched against the roster — an ambiguous name is left for her to pick.
  const voiceFields: VoiceField[] = [
    textField('drug_name', 'Drug name (with form, e.g. Inj. Adrenaline)'),
    numField('dose_quantity', 'Dose quantity (number only)'),
    textField('dose_unit', 'Dose unit — mg, mcg, g, mL, units'),
    selectField('route', 'Route — IV, IM, SC, Oral, Nebulised, Sublingual, Rectal, Topical, Intranasal, IO (intraosseous)', ROUTES),
    selectField('frequency_code', 'Frequency — STAT (once now), SOS (as needed), OD, BD, TDS, QID, Q4H, Q6H, Infusion', FREQ),
    textField('instructions', 'Instructions, e.g. slow IV over 10 minutes'),
    ...(verbal ? [textField('ordered_by', 'Name of the doctor who gave the verbal order')] : []),
  ];
  const applyVoice = (x: Extracted) => setF((cur) => {
    const next = { ...cur };
    const drug = vText(x, 'drug_name'); if (drug) next.drug_name = drug;
    const dose = vNum(x, 'dose_quantity'); if (dose !== undefined && dose > 0) next.dose_quantity = dose;
    const unit = vText(x, 'dose_unit'); if (unit) next.dose_unit = unit;
    const route = vOneOf(x, 'route', ROUTES); if (route) next.route = route;
    const freq = vOneOf(x, 'frequency_code', FREQ); if (freq) next.frequency_code = freq;
    const ins = vText(x, 'instructions'); if (ins) next.instructions = appendText(cur.instructions, ins, '; ');
    if (verbal) {
      const doc = matchByName(vText(x, 'ordered_by'), doctors.data ?? [], (d) => d.full_name);
      if (doc) next.verbal_order_doctor_id = doc.id;
    }
    return next;
  });

  const submit = async () => {
    try {
      await order.mutateAsync({
        ...f,
        dose_quantity: f.dose_quantity === '' ? undefined : f.dose_quantity,
        ...(verbal ? { is_verbal_order: true } : {}),
      });
      setF({ drug_name: '', frequency_code: 'STAT', route: f.route });
      toast({ title: verbal ? 'Verbal order recorded — needs countersignature' : 'Order placed', variant: 'success' });
    } catch (e) { toast({ title: 'Could not place the order', description: errorInfo(e).message, variant: 'danger' }); }
  };

  const doChart = async () => {
    if (!chart) return;
    try {
      await give.mutateAsync({ orderId: chart.order.id, body: { status: chart.status, reason: chart.reason || undefined, dose_given: chart.dose || undefined } });
      setChart(null);
    } catch (e) { toast({ title: 'Could not chart', description: errorInfo(e).message, variant: 'danger' }); }
  };

  const action = (p: Promise<unknown>) => p.catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }));

  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_1.4fr]">
      {open && (canOrder || canAdmin) && (
        <Section title={verbal ? 'Record a verbal order' : 'New medication order'}>
          {verbal && <div className="mb-2 rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">You are recording an order a doctor gave verbally. Name the doctor; it must be countersigned.</div>}
          <VoiceFormFiller fields={voiceFields} onFields={applyVoice} label="🎙 Dictate order"
            hint={verbal ? 'Verbal order from Dr. Menon: Inj. Adrenaline 0.5 mg IM STAT' : 'Inj. Ondansetron 4 mg IV STAT'} />
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Drug" required className="col-span-2">
              <Input value={f.drug_name} onChange={(e) => set('drug_name', e.target.value)} placeholder="e.g. Inj. Adrenaline" />
            </FormField>
            <FormField label="Dose">
              <Input type="number" step="any" value={f.dose_quantity ?? ''} onChange={(e) => set('dose_quantity', e.target.value === '' ? '' : Number(e.target.value))} />
            </FormField>
            <FormField label="Unit">
              <Input value={f.dose_unit ?? ''} onChange={(e) => set('dose_unit', e.target.value)} placeholder="mg / mL / units" />
            </FormField>
            <FormField label="Route">
              <NativeSelect value={f.route ?? ''} onChange={(e) => set('route', e.target.value)}>
                {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Frequency">
              <NativeSelect value={f.frequency_code ?? 'STAT'} onChange={(e) => set('frequency_code', e.target.value)}>
                {FREQ.map((r) => <option key={r} value={r}>{r}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Instructions" className="col-span-2">
              <Input value={f.instructions ?? ''} onChange={(e) => set('instructions', e.target.value)} />
            </FormField>
            {verbal && (
              <>
                <FormField label="Doctor who gave the order" required className="col-span-2">
                  <NativeSelect value={f.verbal_order_doctor_id ?? ''} onChange={(e) => set('verbal_order_doctor_id', e.target.value ? Number(e.target.value) : undefined)}>
                    <option value="">—</option>
                    {(doctors.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
                  </NativeSelect>
                </FormField>
                <label className="col-span-2 flex items-center gap-2 text-[12px]">
                  <Checkbox checked={!!f.administer_now} onCheckedChange={(c) => set('administer_now', c === true)} />
                  I gave this dose now — chart it as given
                </label>
              </>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button loading={order.isPending} disabled={!f.drug_name.trim()} onClick={submit}>{verbal ? 'Record verbal order' : 'Place order'}</Button>
          </div>
        </Section>
      )}

      <Section title="Orders & administrations" className={!open || !(canOrder || canAdmin) ? 'lg:col-span-2' : undefined}>
        {ws.med_orders.length === 0 ? <div className="text-[13px] text-ink-muted">No medication orders.</div> : (
          <div className="space-y-2">
            {ws.med_orders.map((o) => (
              <div key={o.id} className="rounded-lg border border-border p-3 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-[13px]">{o.drug_name}</b>
                  <span>{o.dose_quantity ? `${Number(o.dose_quantity)} ${o.dose_unit ?? ''}` : ''} {o.route ?? ''} · {o.frequency_code}</span>
                  <Badge variant={o.status === 'active' ? 'info' : o.status === 'completed' ? 'success' : 'neutral'}>{o.status}</Badge>
                  {o.is_verbal_order && (o.countersigned_at
                    ? <Badge variant="success">verbal · countersigned by {o.countersigned_by}</Badge>
                    : <Badge variant="warn">verbal ({o.verbal_order_doctor_name}) · not countersigned</Badge>)}
                  <span className="ml-auto text-ink-muted">{timeLabel(o.ordered_at)} · {o.ordered_by}</span>
                </div>
                {o.instructions && <div className="mt-0.5 text-ink-muted">{o.instructions}</div>}
                {o.discontinue_reason && <div className="mt-0.5 italic text-ink-muted">Stopped: {o.discontinue_reason}</div>}
                <div className="mt-1">
                  {o.administrations.length === 0
                    ? <span className={o.status === 'active' ? 'font-semibold text-warn-ink' : 'text-ink-muted'}>Nothing charted</span>
                    : o.administrations.map((a) => (
                      <div key={a.id}>{a.status === 'given' ? '💉' : '⛔'} {timeLabel(a.administered_at)} — {a.status.replace('_', ' ')}{a.dose_given ? ` ${a.dose_given}` : ''}{a.reason ? ` (${a.reason})` : ''} · {a.administered_by}</div>
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {canAdmin && open && o.status === 'active' && (
                    <>
                      <Button size="sm" onClick={() => setChart({ order: o, status: 'given', reason: '', dose: '' })}>Chart given</Button>
                      <Button size="sm" variant="ghost" onClick={() => setChart({ order: o, status: 'not_given', reason: '', dose: '' })}>Not given…</Button>
                    </>
                  )}
                  {canOrder && o.is_verbal_order && !o.countersigned_at && o.status !== 'cancelled' && (
                    <Button size="sm" variant="ghost" loading={sign.isPending} onClick={() => action(sign.mutateAsync(o.id))}>✍ Countersign</Button>
                  )}
                  {canOrder && open && o.status === 'active' && (
                    <Button size="sm" variant="ghost" onClick={() => { stop.reset(); setStopping(o); }}>Stop</Button>
                  )}
                </div>
                {chart?.order.id === o.id && (
                  <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-muted p-2">
                    <NativeSelect value={chart.status} onChange={(e) => setChart({ ...chart, status: e.target.value as MedAdministration['status'] })}>
                      <option value="given">Given</option>
                      <option value="not_given">Not given</option>
                      <option value="held">Held</option>
                      <option value="refused">Refused</option>
                    </NativeSelect>
                    <Input placeholder="Dose given (optional)" value={chart.dose} onChange={(e) => setChart({ ...chart, dose: e.target.value })} />
                    {chart.status !== 'given' && <Input className="col-span-2" placeholder="Reason (required)" value={chart.reason} onChange={(e) => setChart({ ...chart, reason: e.target.value })} />}
                    <div className="col-span-2 flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setChart(null)}>Cancel</Button>
                      <Button size="sm" loading={give.isPending} onClick={doChart}>Save</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
      <TextPromptDialog
        open={!!stopping}
        title={stopping ? `Stop ${stopping.drug_name}?` : ''}
        description="A stopped order can no longer be charted as given. Write a new order to restart it."
        label="Reason for stopping this order"
        confirmLabel="Stop order"
        danger
        pending={stop.isPending}
        error={stop.error ? errorInfo(stop.error).message : null}
        onConfirm={(reason) => {
          if (!stopping) return;
          stop.mutate({ orderId: stopping.id, reason }, { onSuccess: () => setStopping(null) });
        }}
        onClose={() => setStopping(null)}
      />
    </div>
  );
}
